import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { canDelete } from "@/utils/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ClipboardList, Plus, Trash2, Lock, DollarSign, Package, Shield,
  Calendar, User, ChevronLeft, Eye, AlertTriangle, CheckCircle,
} from "lucide-react";

const Inventarios = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState("productos");
  const [inventarios, setInventarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [invDetail, setInvDetail] = useState(null);
  const [settings, setSettings] = useState({});
  const [systemUsers, setSystemUsers] = useState([]);

  const [newForm, setNewForm] = useState({
    nombre: "", fecha_inicio: new Date().toISOString().split("T")[0],
    responsable: "", notas: "",
  });

  const fetchInventarios = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/inventarios?tipo=${tab}`, { withCredentials: true });
      setInventarios(res.data);
    } catch (err) {
      if (err.response?.status !== 403) toast.error("Error al cargar inventarios");
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { fetchInventarios(); }, [fetchInventarios]);
  useEffect(() => {
    axios.get(`${API}/settings`, { withCredentials: true }).then(r => setSettings(r.data)).catch(() => {});
    axios.get(`${API}/employees`, { withCredentials: true }).then(r => setSystemUsers(r.data)).catch(() => {});
  }, []);

  const handleCreate = async () => {
    if (!newForm.nombre.trim()) { toast.error("Ingresa un nombre"); return; }
    try {
      const res = await axios.post(`${API}/inventarios`, { ...newForm, tipo: tab }, { withCredentials: true });
      toast.success("Inventario creado con plantilla");
      setShowNewDialog(false);
      setNewForm({ nombre: "", fecha_inicio: new Date().toISOString().split("T")[0], responsable: "", notas: "" });
      setInvDetail(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const openDetail = async (inv) => {
    try {
      const res = await axios.get(`${API}/inventarios/${inv.id}`, { withCredentials: true });
      setInvDetail(res.data);
    } catch (err) { console.error("Error loading inventario:", err); toast.error("Error al cargar"); }
  };

  const handleDelete = async (id) => {
    if (!confirm("¿Eliminar este inventario?")) return;
    try {
      await axios.delete(`${API}/inventarios/${id}`, { withCredentials: true });
      toast.success("Eliminado"); setInvDetail(null); fetchInventarios();
    } catch (err) { console.error("Error deleting inventario:", err); toast.error("Error"); }
  };

  const handleUpdateConteo = (idx, val) => {
    const lineas = [...(invDetail.lineas || [])];
    const parsed = val === "" ? 0 : parseFloat(val) || 0;
    lineas[idx] = { ...lineas[idx], conteo: parsed, diferencia: parsed - lineas[idx].stock_sistema };
    setInvDetail({ ...invDetail, lineas });
  };

  const handleSave = async () => {
    try {
      const lineas = (invDetail.lineas || []).map(l => ({
        ...l, diferencia: l.conteo - l.stock_sistema,
        costo_total: round(l.conteo * l.costo_unitario),
      }));
      const valor_total = round(lineas.reduce((s, l) => s + l.costo_total, 0));
      await axios.put(`${API}/inventarios/${invDetail.id}`, { lineas, valor_total }, { withCredentials: true });
      toast.success("Inventario guardado");
      openDetail({ id: invDetail.id });
    } catch (err) { console.error("Error saving inventario:", err); toast.error("Error al guardar"); }
  };

  const handleCerrar = async () => {
    if (!confirm("¿Cerrar inventario? El stock se actualizará con los conteos registrados.")) return;
    try {
      const res = await axios.post(`${API}/inventarios/${invDetail.id}/cerrar`, {}, { withCredentials: true });
      toast.success("Inventario cerrado. Stock actualizado.");
      setInvDetail(res.data);
      fetchInventarios();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const round = (n) => Math.round((n || 0) * 100) / 100;
  const fmt = (v) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v || 0);
  const fmtDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }); }
    catch (err) { console.error("Date format error:", err); return d; }
  };

  // ============ DETAIL VIEW ============
  if (invDetail) {
    const lineas = invDetail.lineas || [];
    const isClosed = invDetail.estatus === "cerrado";
    const totalVal = round(lineas.reduce((s, l) => s + (l.conteo * l.costo_unitario), 0));
    // Artículos consumidos: sum of all negative differences (shown as positive)
    const articulosConsumidos = lineas.reduce((s, l) => { const d = l.stock_sistema - l.conteo; return d > 0 ? s + d : s; }, 0);
    // Costo inventario consumido: cost of all consumed items
    const costoConsumido = round(lineas.reduce((s, l) => { const d = l.stock_sistema - l.conteo; return d > 0 ? s + (d * l.costo_unitario) : s; }, 0));

    return (
      <div className="p-4 md:p-6 lg:p-8" data-testid="inventario-detail">
        <div className="max-w-7xl mx-auto space-y-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => { setInvDetail(null); fetchInventarios(); }} data-testid="back-btn">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-800">{invDetail.nombre}</h1>
                <p className="text-sm text-slate-500">
                  {invDetail.tipo === "productos" ? "Productos" : "Activos Fijos"} &middot; {fmtDate(invDetail.fecha_inicio)}
                  {invDetail.responsable && <> &middot; {invDetail.responsable}</>}
                </p>
              </div>
              <Badge className={isClosed ? "bg-red-500 text-white" : "bg-green-500 text-white"}>
                {isClosed ? "Cerrado" : "Abierto"}
              </Badge>
            </div>
            <div className="flex gap-2">
              {!isClosed && (
                <>
                  <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700" data-testid="save-btn">Guardar Conteo</Button>
                  <Button variant="outline" className="text-amber-600 border-amber-300" onClick={handleCerrar} data-testid="cerrar-btn">
                    <Lock className="w-4 h-4 mr-2" /> Cerrar
                  </Button>
                </>
              )}
              {canDelete(user) && <Button variant="ghost" className="text-red-600" onClick={() => handleDelete(invDetail.id)}><Trash2 className="w-4 h-4" /></Button>}
            </div>
          </div>

          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg"><DollarSign className="w-5 h-5 text-blue-600" /></div>
                <div><p className="text-xs text-slate-500">Valor Inventario</p><p className="text-lg font-bold text-blue-700">{fmt(totalVal)}</p></div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 bg-slate-100 rounded-lg"><Package className="w-5 h-5 text-slate-600" /></div>
                <div><p className="text-xs text-slate-500">Items</p><p className="text-lg font-bold">{lineas.length}</p></div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg"><Package className="w-5 h-5 text-amber-600" /></div>
                <div><p className="text-xs text-slate-500">Artículos Consumidos</p><p className="text-lg font-bold text-amber-600">{articulosConsumidos}</p></div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg"><DollarSign className="w-5 h-5 text-red-600" /></div>
                <div>
                  <p className="text-xs text-slate-500">Costo Inventario Consumido</p>
                  <p className="text-lg font-bold text-red-600">{fmt(costoConsumido)}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Template Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-24">CLAVE</TableHead>
                    <TableHead>DESCRIPCIÓN</TableHead>
                    <TableHead>ARTÍCULO</TableHead>
                    <TableHead className="text-center w-28">STOCK SISTEMA</TableHead>
                    <TableHead className="text-center w-28">CONTEO</TableHead>
                    <TableHead className="text-center w-28">DIFERENCIA</TableHead>
                    <TableHead className="text-center w-28">STOCK FINAL</TableHead>
                    <TableHead className="text-right w-32">COSTO UNIT.</TableHead>
                    <TableHead className="text-right w-32">VALOR CONTEO</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lineas.map((l, idx) => {
                    const dif = l.conteo - l.stock_sistema;
                    const stockFinal = l.stock_sistema - (l.stock_sistema - l.conteo);
                    return (
                      <TableRow key={l.item_id} className={dif !== 0 ? (dif < 0 ? "bg-red-50/60" : "bg-green-50/60") : ""}>
                        <TableCell className="font-mono font-semibold text-sm">{l.item_clave}</TableCell>
                        <TableCell className="text-sm">{l.item_descripcion}</TableCell>
                        <TableCell className="text-sm text-slate-500">{l.item_articulo}</TableCell>
                        <TableCell className="text-center font-semibold">{l.stock_sistema}</TableCell>
                        <TableCell className="text-center">
                          {isClosed ? (
                            <span className="font-bold">{l.conteo}</span>
                          ) : (
                            <Input
                              type="number" min="0" step="0.5"
                              value={l.conteo === 0 ? "" : l.conteo}
                              onChange={(e) => handleUpdateConteo(idx, e.target.value)}
                              onFocus={(e) => { if (e.target.value === "0") e.target.value = ""; }}
                              className="w-20 mx-auto text-center font-bold h-8"
                              data-testid={`conteo-${idx}`}
                            />
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={`font-bold ${dif < 0 ? "text-red-600" : dif > 0 ? "text-green-600" : "text-slate-400"}`}>
                            {dif > 0 ? `+${dif}` : dif}
                          </span>
                        </TableCell>
                        <TableCell className="text-center font-semibold">{l.conteo}</TableCell>
                        <TableCell className="text-right text-sm">{fmt(l.costo_unitario)}</TableCell>
                        <TableCell className="text-right font-semibold text-sm">{fmt(l.conteo * l.costo_unitario)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ============ LIST VIEW ============
  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="inventarios-page">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Inventarios</h1>
            <p className="text-slate-500">Conteo físico y valuación</p>
          </div>
          <Button onClick={() => setShowNewDialog(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-inv-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuevo Inventario
          </Button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="productos" className="gap-2" data-testid="tab-productos"><Package className="w-4 h-4" /> Productos</TabsTrigger>
            <TabsTrigger value="activos_fijos" className="gap-2" data-testid="tab-activos"><Shield className="w-4 h-4" /> Activos Fijos</TabsTrigger>
          </TabsList>
          <TabsContent value={tab} className="mt-4">
            {loading ? (
              <div className="text-center py-10 text-slate-400">Cargando...</div>
            ) : inventarios.length === 0 ? (
              <Card><CardContent className="py-16 text-center text-slate-400">
                <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-lg font-medium">Sin inventarios</p>
                <p className="text-sm">Crea tu primer inventario para comenzar el conteo</p>
              </CardContent></Card>
            ) : (
              <div className="grid gap-4">
                {inventarios.map(inv => (
                  <Card key={inv.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => openDetail(inv)} data-testid={`inv-card-${inv.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-lg ${inv.estatus === "cerrado" ? "bg-red-100" : "bg-emerald-100"}`}>
                            {inv.estatus === "cerrado" ? <CheckCircle className="w-6 h-6 text-red-600" /> : <ClipboardList className="w-6 h-6 text-emerald-600" />}
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-800">{inv.nombre}</h3>
                            <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmtDate(inv.fecha_inicio)}</span>
                              {inv.responsable && <span className="flex items-center gap-1"><User className="w-3 h-3" /> {inv.responsable}</span>}
                              <span>{inv.total_items || 0} items</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <p className="text-xs text-slate-400">Valor</p>
                            <p className="text-lg font-bold text-blue-700">{fmt(inv.valor_total)}</p>
                          </div>
                          <Badge className={inv.estatus === "cerrado" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}>
                            {inv.estatus === "cerrado" ? "Cerrado" : "Abierto"}
                          </Badge>
                          <Eye className="w-5 h-5 text-slate-400" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* New Inventory Dialog */}
        <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Nuevo Inventario — {tab === "productos" ? "Productos" : "Activos Fijos"}</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-500">Se generará una plantilla con todos los {tab === "productos" ? "productos" : "activos"} para que registres el conteo físico.</p>
            <div className="space-y-4">
              <div className="space-y-2"><Label>Nombre *</Label><Input value={newForm.nombre} onChange={e => setNewForm(p => ({ ...p, nombre: e.target.value }))} placeholder="Ej: Inventario Abril 2026" data-testid="new-inv-nombre" /></div>
              <div className="space-y-2"><Label>Fecha</Label><Input type="date" value={newForm.fecha_inicio} onChange={e => setNewForm(p => ({ ...p, fecha_inicio: e.target.value }))} /></div>
              <div className="space-y-2">
                <Label>Responsable</Label>
                <Select value={newForm.responsable || "none"} onValueChange={v => setNewForm(p => ({ ...p, responsable: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="responsable-select"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Sin asignar --</SelectItem>
                    {systemUsers.filter(u => u.is_active !== false).map(u => <SelectItem key={u.id} value={u.nombre}>{u.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Notas</Label><Input value={newForm.notas} onChange={e => setNewForm(p => ({ ...p, notas: e.target.value }))} placeholder="Opcional..." /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewDialog(false)}>Cancelar</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleCreate} data-testid="create-inv-btn">Crear Inventario</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Inventarios;
